import { signal } from '@preact/signals';
import { useLocation } from 'preact-iso';
import { useEffect, useMemo, useRef } from 'preact/hooks';
import toast from 'react-hot-toast';
import useSWR from 'swr';
import AuthenticatedPage from '../../components/authenticated-page';
import { ButtonBar } from '../../components/button-bar';
import { ContinueListeningToEpisodes } from '../../components/continue-listening-section';
import { Icon } from '../../components/icon';
import LatestEpisodes from '../../components/latest-episodes';
import LatestPodcasts from '../../components/latest-podcasts';
import { ListenLaterTabContents } from '../../components/listen-later-section';
import { LoadingBar } from '../../components/loading-bar';
import { useSessionStore } from '../../global-state/session';
import { useUserDataStore } from '../../global-state/user-data';
import { fetchEpisodes } from '../../utils/api';
import { classNames, isOnServer } from '../../utils/helpers';
import { usePageTitle } from '../../utils/page-title';

export default function ListenNow() {
  usePageTitle('Listen Now');
  useWelcomeUserAfterPlanPurchase();

  const followedPodcastIds = useUserDataStore((s) => s.followedPodcastIds);
  const showEmptyState = followedPodcastIds.length === 0 && !isOnServer;
  return (
    <AuthenticatedPage>
      {/* Re-use the same class to help w/ hydration mismatches */}
      <div class="top-section-side-padding-children">
        {showEmptyState ? (
          <div class="flex flex-col">
            <EmptyStateTemplate />
          </div>
        ) : (
          <>
            <div class="flex flex-col">
              <LatestPodcasts />
              <ContinueListeningToEpisodes />
            </div>
            <MainTabs />
          </>
        )}
      </div>
    </AuthenticatedPage>
  );
}

// Save outside of component to restore when page is back in view
const listenNowTab = signal<'latest_episodes' | 'listen_later'>('latest_episodes');

function MainTabs() {
  const isPageBehindVeil = useSessionStore((s) => s.isPageBehindVeil);

  const tabNavOffsetTop = useRef<number>();

  const followedPodcastIds = useUserDataStore((s) => s.followedPodcastIds);
  let { isLoading } = useSWR(
    ['latest-episodes-of-followed-podcasts', followedPodcastIds],
    () => {
      return Promise.all(followedPodcastIds.map((id) => fetchEpisodes(id, 'latest')));
    }
  );

  const tabButtons = useMemo(
    () =>
      [
        {
          id: 'latest_episodes',
          children: <>Latest Episodes</>,
        },
        {
          id: 'listen_later',
          children: (
            <>
              Listen Later{' '}
              <Icon class="inline h-5 w-5 text-icon-blue" sprite="play-later" />
            </>
          ),
        },
      ] as const,
    [isLoading]
  );

  return (
    <>
      <nav
        class={classNames(
          'sticky top-[var(--safe-area-top)] flex h-[var(--header-ui-height)] items-center',
          'bg-slate-100/85 backdrop-blur',
          isPageBehindVeil ? 'z-0' : 'z-[11]'
        )}
        role="tablist"
        ref={(el) => {
          if (el && !tabNavOffsetTop.current) {
            tabNavOffsetTop.current = el.offsetTop;
          }
        }}
      >
        <ButtonBar
          buttons={tabButtons}
          selectedId={listenNowTab.value}
          onSelect={(tab) => {
            listenNowTab.value = tab;
            if (typeof tabNavOffsetTop.current === 'number')
              scrollToTopOfSection(tabNavOffsetTop.current);
          }}
          buttonClassName="h-9 [&_span]:rounded-b-none [&_span]:rounded-t-lg px-4"
        />
        <LoadingBar isLoading={isLoading} className="absolute bottom-0 left-0" />
      </nav>
      {listenNowTab.value === 'latest_episodes' ? (
        <LatestEpisodes podcastIds={followedPodcastIds} />
      ) : (
        <ListenLaterTabContents />
      )}
    </>
  );
}

function EmptyStateTemplate() {
  const { route } = useLocation();
  return (
    <>
      <h1 class="h1">Welcome!</h1>
      <p>
        Search for podcasts and press the{' '}
        <button class="button button--favorite button--inline">
          <span>Follow</span>
          <Icon sprite="plus-circle" />
        </button>{' '}
        button to add them here. We'll keep track of these podcasts and let you know
        when there's new episodes!
      </p>
      <button
        class="button button--transparent"
        style={{
          '--button-height': '36px',
          fontSize: '16px',
          margin: '8px auto 0 0',
        }}
        onClick={() => route('/search')}
      >
        Search for Podcasts
      </button>
    </>
  );
}

function scrollToTopOfSection(maxScrollTop: number) {
  const scrollContainer = window.pageScrollContainer;
  if (scrollContainer.scrollTop > maxScrollTop) {
    scrollContainer.scrollTop = maxScrollTop;
  }
}

function useWelcomeUserAfterPlanPurchase() {
  useEffect(() => {
    if (location.hash === '#plan-purchase-successful') {
      toast.success('Thanks for purchasing a plan!', {
        position: 'top-center',
      });
    }
  }, []);
}
